import type { StoreApi } from 'zustand';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import type {
  GlobalStateStore,
  GlobalStateStoreType,
  ManuallyTriggeredDataSourcesSetterType,
} from '../types';
import { DependencyFlowStatus } from '../types';
import DependencyFlowHelper from '../helpers/DependencyFlowHelper';
import type { DataSourceRecordStoreState } from '../../DataSourceRecordStore';
import getIntraDependenciesActions from './intraDependenciesActions';
import getDependencyFlowActions from './dependencyFlowActions';

/**
 * these actions are business login driven,
 * when you need an action in store related to a data source, but it is not a simple setter or getter
 * adds a new action here and uses it in your component
 */
const getDataSourceActions = ({
  get,
  set,
  setPreviewTriggeredDataSource,
  getDataSourceRecordsStore,
  setManuallyTriggeredDataSource,
  setRunTriggeredDataSource,
  removeDataSourceFromGlobalState,
}: {
  set: StoreApi<GlobalStateStore>['setState'];
  get: StoreApi<GlobalStateStore>['getState'];
  removeDataSourceFromGlobalState: (dataSourceId: string) => void;
  getDataSourceRecordsStore: StoreApi<DataSourceRecordStoreState>['getState'];
  setPreviewTriggeredDataSource: (id: string, value: boolean) => void;
  setManuallyTriggeredDataSource: ManuallyTriggeredDataSourcesSetterType['setSingle'];
  setRunTriggeredDataSource: (id: string, value: boolean) => void;
}) => {
  const { setIntraDependencies, setIntraDependenciesSingle, removeInterDependencies } =
    getIntraDependenciesActions(set);

  const { setEntitiesDependencyFlow, setEntityIdDependencyFlow, removeEntityIdDependencyFlow } =
    getDependencyFlowActions(set, get);

  const dataSourceActions: GlobalStateStoreType['actions']['datasource'] = {
    triggerManualDataSource: (id, value) => {
      const intraDependencies = get().intraDependencies.dataSources;
      const dependencyFlow = get().dependencyFlowManager.dataSources;
      const completeDatasourceDependencyFlow = DependencyFlowHelper.getDependencyFlowStatus({
        dependencyFlowInstance: dependencyFlow,
        entities: {
          [value.dataSourceId]: {
            status: DependencyFlowStatus.Staging,
          },
        },
        intraDependencies,
      });

      // remove id from completeDatasourceDependencyFlow
      const { [value.dataSourceId]: _, ...rest } = completeDatasourceDependencyFlow;

      setEntitiesDependencyFlow({
        entities: rest,
        key: 'dataSources',
      });

      setManuallyTriggeredDataSource(id, value);
    },
    previewDataSource: (id, value) => {
      if (!value) {
        return setPreviewTriggeredDataSource(id, value);
      }

      // we are building a dependency graph
      // because we want to know the latest dependencies of this data source when the preview is triggered
      const intraDependencies = DataSourceHelper.getPreviewDataSourceTentativeDependencies(
        id,
        getDataSourceRecordsStore,
      );

      const dependencyFlow = get().dependencyFlowManager.dataSources;

      // now we have put all the data sources in to staging which would be required to fulfill the preview input
      const completeDatasourceDependencyFlow = DependencyFlowHelper.getDependencyFlowStatus({
        dependencyFlowInstance: dependencyFlow,
        entities: {
          [id]: {
            status: DependencyFlowStatus.Staging,
          },
        },
        intraDependencies,
      });

      // remove id from completeDatasourceDependencyFlow
      const { [id]: _, ...rest } = completeDatasourceDependencyFlow;
      setEntitiesDependencyFlow({
        entities: rest,
        key: 'dataSources',
      });
      setPreviewTriggeredDataSource(id, value);
      // setting run to false, because after previewing a datasource in the preview panel we want to now use the preview data
      setRunTriggeredDataSource(id, false);
    },
    saveAndRunDataSource: (id, value) => {
      setRunTriggeredDataSource(id, value);
    },
    removeDataSourceFromStore: (id: string) => {
      // TODO: check for delete data source
      removeDataSourceFromGlobalState(id);
      removeEntityIdDependencyFlow({
        entityId: id,
        key: 'dataSources',
      });
      removeInterDependencies({
        entityId: id,
        key: 'dataSources',
      });
    },
    removeDependencyFlow: (entityId: string) => {
      removeEntityIdDependencyFlow({
        entityId,
        key: 'dataSources',
      });
    },
    addDependencyFlow: (entityId, status) => {
      setEntityIdDependencyFlow({
        entityId,
        key: 'dataSources',
        status,
      });
    },
    addIntraDependencies: (entities) => {
      setIntraDependencies({
        dependencyGraph: entities,
        key: 'dataSources',
      });
    },
    addDependencyFlows: (entities) => {
      setEntitiesDependencyFlow({
        entities,
        key: 'dataSources',
      });
    },
    addIntraDependency: (entityId, dependencies) => {
      setIntraDependenciesSingle({
        entityId,
        dependencies,
        key: 'dataSources',
      });

      // now that dependencies are changed, check if this datasource is non staging,
      const dependencyFlow = get().dependencyFlowManager.dataSources;
      if (!DependencyFlowHelper.isDependencyStaging(dependencyFlow, entityId)) {
        const intraDependencies = get().intraDependencies.dataSources;
        // then we will filter out all the dependencies that are in unstaged,
        // and put them to staging to fetch their data,
        // intent is to get all the dependencies that are unstaged
        const completeDatasourceDependencyFlow = DependencyFlowHelper.getDependencyFlowStatus({
          dependencyFlowInstance: dependencyFlow,
          entities: {
            [entityId]: {
              status: DependencyFlowStatus.Staging,
            },
          },
          intraDependencies,
        });
        // remove id from completeDatasourceDependencyFlow because it adds itself
        const { [entityId]: _, ...rest } = completeDatasourceDependencyFlow;

        // because this datasource is automatic,
        // after fetching data from new dependencies, it will automatically refetch itself
        setEntitiesDependencyFlow({
          entities: rest,
          key: 'dataSources',
        });
      }
    },
  };

  return dataSourceActions;
};

export default getDataSourceActions;
